'use client';

import { Button, TextArea } from '@common/components';
import { useToast } from '@common/hooks';
import { zodResolver } from '@common/utils';
import { cn, z } from '@core/utils';
import { Controller, useForm } from 'react-hook-form';
import { FileUploadPlusBox } from '@/app/_components/FileUploadPlusBox';
import { RateInput } from '@/app/donation/(private)/my/review/_components/RateInput';
export interface ReviewFormData {
  rating: number;
  contents: string;
  imageUrlList: string[];
}
const schema = z.object({
  rating: z.number({
    error: '별점을 선택해주세요.'
  }).min(1, '별점을 선택해주세요.'),
  contents: z.string({
    error: '후기는 최소 10글자 이상 작성해주세요.'
  }).min(10, '후기는 최소 10글자 이상 작성해주세요.'),
  imageUrlList: z.array(z.string())
});
interface ReviewFormProps {
  onSubmit?: (data: ReviewFormData) => void;
  onCancel?: () => void;
  defaultValues?: ReviewFormData;
  isLoading?: boolean;
  guideMessage?: string;
}
export const ReviewForm = ({
  onSubmit,
  onCancel,
  defaultValues,
  isLoading,
  guideMessage = '작성하신 후기는 나의기부 > 나의 답례품 후기에서 확인하실 수 있어요.'
}: ReviewFormProps) => {
  const {
    toast
  } = useToast();
  const {
    control,
    handleSubmit,
    formState: {
      errors
    },
    setValue,
    watch
  } = useForm<ReviewFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaultValues ?? {
      rating: 0,
      contents: '',
      imageUrlList: []
    }
  });

  // eslint-disable-next-line react-hooks/incompatible-library -- react-hook-form watch는 컴파일러 미호환
  const imageUrlList = watch('imageUrlList');
  const sendSubmit = (formData: ReviewFormData) => {
    onSubmit?.(formData);
  };
  return <div className="flex grow flex-col">
      <div className={cn('flex flex-col gap-32 pb-24', 'mobile-tablet:grow mobile-tablet:px-20')}>
        <div>
          <p className="mb-8 font-500 text-Gray-900 typo-body4">
            답례품 만족도는 어떤가요?
          </p>
          <Controller name="rating" control={control} render={({
          field
        }) => <RateInput value={field.value} onChange={field.onChange} />} />
          {errors.rating ? <p className="mt-4 text-R-500 typo-caption1">
              {errors.rating.message}
            </p> : null}
        </div>
        <div className="flex flex-col gap-4">
          <Controller name="contents" control={control} render={({
          field: field_0
        }) => <TextArea placeholder="답례품 후기를 작성해주세요." {...field_0} rows={10} className={cn('h-auto w-full border-Gray-300', {
          'border-R-500': errors.contents
        })} />} />
          {errors.contents ? <p className="text-R-500 typo-caption1">
              {errors.contents.message}
            </p> : null}
        </div>
        <div>
          <p className="mb-8 font-400 text-Gray-800 typo-body4">첨부파일</p>
          <ul className="mb-16 flex flex-wrap gap-8">
            {imageUrlList?.map((imageUrl, index) => <li key={imageUrl}>
                <FileUploadPlusBox type="HOME_ORDER_PRODUCT_REVIEW" imageUrl={imageUrl} onDelete={() => {
              // 클릭한 이미지의 인덱스를 사용하여 해당 이미지만 필터링
              setValue('imageUrlList', imageUrlList.filter((_, i) => i !== index));
            }} previewBlank />
              </li>)}

            {/** 업로드 영역 */}
            <FileUploadPlusBox type="HOME_ORDER_PRODUCT_REVIEW" limitByte={10 * 1024 * 1024} onUpload={imageUrl_0 => {
            if (!imageUrl_0) {
              toast({
                message: '이미지 업로드를 실패했습니다.',
                type: 'fail'
              });
              return;
            }
            setValue('imageUrlList', [...imageUrlList, imageUrl_0]);
          }} />
          </ul>
          <ul className="flex list-none flex-col pl-5">
            <li className={cn("flex min-h-20 text-start font-400 text-Gray-600 typo-body4 before:mr-6 before:content-['•'] before:typo-body4", 'mobile-tablet:typo-caption1 mobile-tablet:before:typo-caption1')}>
              이미지는 당 10MB 이내의 이미지 파일(jpg, jpeg,gif,png)만 첨부할 수
              있습니다.
            </li>
            <li className={cn("flex min-h-20 text-start font-400 text-Gray-600 typo-body4 before:mr-6 before:content-['•'] before:typo-body4", 'mobile-tablet:typo-caption1 mobile-tablet:before:typo-caption1')}>
              이미지는 최대 5장까지 첨부 가능합니다.
            </li>
          </ul>
        </div>
        <div>
          {/* divider */}
          <div className="border-t border-Gray-200" />
          <p className={cn('mt-16 text-center font-400 text-Gray-600 typo-body4', 'mobile-tablet:text-left')}>
            {guideMessage}
          </p>
        </div>
      </div>
      <footer className={cn('gap-8 bg-Gray-white px-20 py-12 flex-center', 'mobile-tablet:sticky mobile-tablet:bottom-0 mobile-tablet:mt-auto mobile-tablet:shadow-lg')}>
        <Button theme="sub-line" className="basis-[236px] mobile-tablet:flex-1" onClick={onCancel}>
          취소
        </Button>
        <Button theme="primary-fill" className="basis-[236px] mobile-tablet:flex-1" onClick={handleSubmit(sendSubmit)} loading={isLoading}>
          저장
        </Button>
      </footer>
    </div>;
};